import { existsSync, readFileSync, unlinkSync, writeFileSync } from "node:fs";
import { join, resolve } from "node:path";
import type { SessionListRecord } from "@core/features/session/index.js";
import { ensureDir, expandHome, safeFilename } from "@core/shared/lib/core-utils/index.js";
import { SessionListIndexStore } from "./session-list-index.store.js";
import { normalizeSessionHistoryWindow } from "@core/features/session/utils/session-history-window.utils.js";

export type SessionMessage = {
  role: string;
  content: unknown;
  timestamp: string;
  [key: string]: unknown;
};

export type SessionEvent = {
  seq: number;
  type: string;
  timestamp: string;
  data: Record<string, unknown>;
};

export type Session = {
  key: string;
  agentId?: string;
  messages: SessionMessage[];
  events: SessionEvent[];
  nextSeq: number;
  createdAt: Date;
  updatedAt: Date;
  metadata: Record<string, unknown>;
};

export type SessionStoreOptions = {
  sessionsDir: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function toIsoString(value: unknown, fallback: string): string {
  if (typeof value !== "string") {
    return fallback;
  }
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return new Date(parsed).toISOString();
}

function toOptionalAgentId(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : undefined;
}

type SessionLoadState = {
  events: SessionEvent[];
  agentId?: string;
  metadata: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
  fallbackSeq: number;
};

function createSessionLoadState(): SessionLoadState {
  return {
    events: [],
    metadata: {},
    createdAt: new Date(),
    updatedAt: new Date(),
    fallbackSeq: 1,
  };
}

function appendSessionEvent(state: SessionLoadState, event: SessionEvent): void {
  state.events.push(event);
  state.fallbackSeq = Math.max(state.fallbackSeq, event.seq + 1);
}

function applyMetadataLine(state: SessionLoadState, data: Record<string, unknown>): boolean {
  if (data._type !== "metadata") {
    return false;
  }
  state.agentId = toOptionalAgentId(data.agent_id);
  state.metadata = isRecord(data.metadata) ? data.metadata : {};
  if (data.created_at) {
    state.createdAt = new Date(String(data.created_at));
  }
  if (data.updated_at) {
    state.updatedAt = new Date(String(data.updated_at));
  }
  return true;
}

function applyEventLine(state: SessionLoadState, data: Record<string, unknown>): boolean {
  if (data._type !== "event") {
    return false;
  }
  const rawSeq = Number(data.seq);
  const seq = Number.isFinite(rawSeq) && rawSeq > 0 ? Math.trunc(rawSeq) : state.fallbackSeq;
  const timestamp = toIsoString(data.timestamp, new Date().toISOString());
  const type = typeof data.type === "string" && data.type.trim() ? data.type.trim() : "message.other";
  const payload = isRecord(data.data) ? data.data : {};
  appendSessionEvent(state, { seq, type, timestamp, data: payload });
  return true;
}

function createLegacyMessage(data: Record<string, unknown>): SessionMessage {
  const legacyRole = typeof data.role === "string" ? data.role : "assistant";
  const legacyTimestamp = toIsoString(data.timestamp, new Date().toISOString());
  return {
    ...data,
    role: legacyRole,
    timestamp: legacyTimestamp,
    content: Object.prototype.hasOwnProperty.call(data, "content") ? data.content : "",
  } as SessionMessage;
}

function applyLegacyLine(
  state: SessionLoadState,
  data: Record<string, unknown>,
  resolveMessageEventType: (message: SessionMessage) => string,
): void {
  const message = createLegacyMessage(data);
  appendSessionEvent(state, {
    seq: state.fallbackSeq,
    type: resolveMessageEventType(message),
    timestamp: message.timestamp,
    data: { message },
  });
}

function sortSessionEvents(events: SessionEvent[]): void {
  events.sort((left, right) => {
    if (left.seq !== right.seq) {
      return left.seq - right.seq;
    }
    return Date.parse(left.timestamp) - Date.parse(right.timestamp);
  });
}

function buildLoadedSession(params: {
  key: string;
  state: SessionLoadState;
  projectMessageFromEvent: (event: SessionEvent) => SessionMessage | null;
}): Session {
  const { key, projectMessageFromEvent, state } = params;
  sortSessionEvents(state.events);
  const messages = state.events
    .map((event) => projectMessageFromEvent(event))
    .filter((message): message is SessionMessage => Boolean(message));
  const latestTs =
    state.events.length > 0 ? state.events[state.events.length - 1]?.timestamp : null;
  const updatedAt = latestTs ? new Date(latestTs) : state.updatedAt;
  const nextSeq = state.events.reduce((maxSeq, event) => Math.max(maxSeq, event.seq), 0) + 1;

  return {
    key,
    ...(state.agentId ? { agentId: state.agentId } : {}),
    messages,
    events: state.events,
    nextSeq,
    createdAt: state.createdAt,
    updatedAt,
    metadata: state.metadata,
  };
}

function resolveSessionDirectory(options: SessionStoreOptions): string {
  return ensureDir(resolve(expandHome(options.sessionsDir.trim())));
}

export class SessionStore {
  private sessionsDir: string;
  private cache: Map<string, Session> = new Map();
  private listIndex: SessionListIndexStore;

  constructor(options: SessionStoreOptions) {
    this.sessionsDir = resolveSessionDirectory(options);
    this.listIndex = new SessionListIndexStore(this.sessionsDir);
  }

  private getSessionPath = (key: string): string => {
    const safeKey = safeFilename(key.replace(/:/g, "_"));
    return join(this.sessionsDir, `${safeKey}.jsonl`);
  };

  getOrCreate = (key: string): Session => {
    const cached = this.cache.get(key);
    if (cached) {
      return cached;
    }
    const loaded = this.load(key);
    const session = loaded ?? {
      key,
      messages: [],
      events: [],
      nextSeq: 1,
      createdAt: new Date(),
      updatedAt: new Date(),
      metadata: {}
    };
    this.cache.set(key, session);
    return session;
  };

  getIfExists = (key: string): Session | null => {
    const cached = this.cache.get(key);
    if (cached) {
      return cached;
    }
    const loaded = this.load(key);
    if (loaded) {
      this.cache.set(key, loaded);
    }
    return loaded;
  };

  appendEvent = (
    session: Session,
    params: {
      type: string;
      data?: Record<string, unknown>;
      timestamp?: string;
    }
  ): SessionEvent => {
    const timestamp = toIsoString(params.timestamp, new Date().toISOString());
    const event: SessionEvent = {
      seq: session.nextSeq,
      type: params.type,
      timestamp,
      data: params.data ?? {}
    };
    session.nextSeq += 1;
    session.events.push(event);

    const projected = this.projectMessageFromEvent(event);
    if (projected) {
      session.messages.push(projected);
    }

    session.updatedAt = new Date(timestamp);
    return event;
  };

  addMessage = (session: Session, role: string, content: unknown, extra: Record<string, unknown> = {}): SessionEvent => {
    const msg: SessionMessage = {
      role,
      content,
      timestamp: new Date().toISOString(),
      ...extra
    };

    const eventType = this.resolveMessageEventType(msg);
    return this.appendEvent(session, {
      type: eventType,
      timestamp: msg.timestamp,
      data: { message: msg }
    });
  };

  getHistory = (session: Session, maxMessages = 50): Array<Record<string, unknown>> => {
    const recent = session.messages.length > maxMessages ? session.messages.slice(-maxMessages) : session.messages;
    const normalized = this.normalizeHistoryWindow(recent);
    return normalized.map((msg) => {
      const entry: Record<string, unknown> = {
        role: msg.role,
        content: msg.content
      };
      if (typeof msg.name === "string") {
        entry.name = msg.name;
      }
      if (typeof msg.tool_call_id === "string") {
        entry.tool_call_id = msg.tool_call_id;
      }
      if (Array.isArray(msg.tool_calls)) {
        entry.tool_calls = msg.tool_calls;
      }
      if (typeof msg.reasoning_content === "string" && msg.reasoning_content) {
        entry.reasoning_content = msg.reasoning_content;
      }
      return entry;
    });
  };

  private normalizeHistoryWindow = (messages: SessionMessage[]): SessionMessage[] => normalizeSessionHistoryWindow(messages);

  clear = (session: Session): void => {
    session.events = [];
    session.messages = [];
    session.nextSeq = 1;
    session.updatedAt = new Date();
  };

  private projectMessageFromEvent = (event: SessionEvent): SessionMessage | null => {
    const source = isRecord(event.data.message)
      ? event.data.message
      : isRecord(event.data)
        ? event.data
        : null;
    if (!source) {
      return null;
    }

    const role = typeof source.role === "string" ? source.role : "";
    if (!role) {
      return null;
    }

    const timestamp = toIsoString(source.timestamp, event.timestamp);
    return {
      ...source,
      role,
      timestamp,
      content: Object.prototype.hasOwnProperty.call(source, "content") ? source.content : ""
    };
  };

  private resolveMessageEventType = (message: SessionMessage): string => {
    const role = typeof message.role === "string" ? message.role.trim().toLowerCase() : "";
    if (role === "assistant" && Array.isArray(message.tool_calls) && message.tool_calls.length > 0) {
      return "assistant.tool_call";
    }
    if (role === "tool") {
      return "tool.result";
    }
    if (role === "assistant") {
      return "message.assistant";
    }
    if (role === "user") {
      return "message.user";
    }
    if (role === "system") {
      return "message.system";
    }
    return `message.${role || "other"}`;
  };

  private load = (key: string): Session | null => {
    const path = this.getSessionPath(key);
    if (!existsSync(path)) {
      return null;
    }
    try {
      const lines = readFileSync(path, "utf-8").split("\n").filter(Boolean);
      const state = createSessionLoadState();

      for (const line of lines) {
        const data = JSON.parse(line) as Record<string, unknown>;
        if (applyMetadataLine(state, data) || applyEventLine(state, data)) {
          continue;
        }
        applyLegacyLine(state, data, this.resolveMessageEventType);
      }
      return buildLoadedSession({
        key,
        state,
        projectMessageFromEvent: this.projectMessageFromEvent,
      });
    } catch {
      return null;
    }
  };

  save = (session: Session): void => {
    const path = this.getSessionPath(session.key);
    const lastMessageAt = session.messages.at(-1)?.timestamp;
    const metadataLine = {
      _type: "metadata",
      created_at: session.createdAt.toISOString(),
      updated_at: session.updatedAt.toISOString(),
      ...(session.agentId ? { agent_id: session.agentId } : {}),
      message_count: session.messages.length,
      ...(lastMessageAt ? { last_message_at: lastMessageAt } : {}),
      metadata: session.metadata
    };
    const eventLines = session.events.map((event) =>
      JSON.stringify({
        _type: "event",
        seq: event.seq,
        type: event.type,
        timestamp: event.timestamp,
        data: event.data
      })
    );
    const lines = [JSON.stringify(metadataLine), ...eventLines].join("\n");
    writeFileSync(path, `${lines}\n`);
    this.cache.set(session.key, session);
    this.listIndex.upsertSession(session, path);
  };

  delete = (key: string): boolean => {
    this.cache.delete(key);
    const path = this.getSessionPath(key);
    if (existsSync(path)) {
      unlinkSync(path);
      this.listIndex.removeSession(key);
      return true;
    }
    return false;
  };

  listSessions = (): SessionListRecord[] => {
    return this.listIndex.listSessions();
  };
}
